import actionTypes from './types';
import { schedule } from '../utils/scheduler';
import PERFORMANCE from '../config/performance';

const {
  TRADES_UPDATE_MINIMUM_INTERVAL,
  DEPTH_UPDATE_MINIMAL_INTERVAL,
  EVENT_LOOP_TOLERANCE,
  MAX_WAIT,
  TRADES_LIMIT,
  DEPTH_LIMIT,
} = PERFORMANCE;

const {
  WEB_SOCKET_CONNECTED,
  WEB_SOCKET_DISCONNECTED,
  WEB_SOCKET_RECEIVED,
  WEB_SOCKET_RECEIVED_BATCH,
  WEB_SOCKET_QUEUE,
  WEB_SOCKET_FLUSH,
  WEB_SOCKET_DISCONNECT,
  WEB_SOCKET_DEPTH_DISCONNECTED,
  WEB_SOCKET_DEPTH_CONNECTED,

  WEB_SOCKET_RECEIVED_DEPTH,
  WEB_SOCKET_RECEIVED_TRADE,
  WEB_SOCKET_RECEIVED_TICKERS,
  WEB_SOCKET_MARGIN_UPDATE,
} = actionTypes;

const dispatchNoPayload = type => () => dispatch => dispatch({ type });
const dispatchWithPayload = type => payload => dispatch => dispatch({
  type, payload,
});

export const socketConnected = dispatchNoPayload(WEB_SOCKET_CONNECTED);

export const socketDisconnected = dispatchNoPayload(WEB_SOCKET_DISCONNECTED);

export const socketDisconnect = dispatchNoPayload(WEB_SOCKET_DISCONNECT);

export const depthSocketConnected =
  dispatchNoPayload(WEB_SOCKET_DEPTH_CONNECTED);

export const depthSocketDisconnected =
  dispatchNoPayload(WEB_SOCKET_DEPTH_DISCONNECTED);

export const socketReceived = dispatchWithPayload(WEB_SOCKET_RECEIVED);

export const socketReceivedBatch = dispatchWithPayload(WEB_SOCKET_RECEIVED_BATCH);

export const socketReceivedMarginUpdate = dispatchWithPayload(WEB_SOCKET_MARGIN_UPDATE);

export const socketQueue = dispatchWithPayload(WEB_SOCKET_QUEUE);

export const socketFlush = dispatchWithPayload(WEB_SOCKET_FLUSH);

const dispatchScheduled = type => (payload, dispatch) => dispatch({
  type, payload,
});

const isArr = element => Array.isArray(element);

// #2140
// Limit the action dispatching so that the CPU will not explode
export const socketReceivedDepth =
  schedule(dispatchScheduled(WEB_SOCKET_RECEIVED_DEPTH), {
    wait: DEPTH_UPDATE_MINIMAL_INTERVAL,
    tolerance: EVENT_LOOP_TOLERANCE,
    maxWait: MAX_WAIT * 2,
    init: () => ({
      symbol: null,
      data: {
        asks: [], bids: [], lastUpdateId: null, firstUpdateId: 0,
      },
    }),
    collect: (datum, depths) => {
      depths.symbol = datum.s;
      depths.data.asks.push(...datum.a);
      depths.data.bids.push(...datum.b);
      // filter
      if (isArr(depths.data.asks) && depths.data.asks?.length > 8) {
        const askKeys = {};
        for (let i = depths.data.asks.length - 1; i >= 0; i--) {
          const key = depths.data.asks[i][0];
          if (askKeys[key]) depths.data.asks.splice(i, 1);
          askKeys[key] = key;
        }
        if (depths.data.asks.length > DEPTH_LIMIT) depths.data.asks.splice(0, depths.data.asks.length - DEPTH_LIMIT);
      }

      if (isArr(depths.data.bids) && depths.data.bids?.length > 8) {
        const bidKeys = {};
        for (let i = depths.data.bids.length - 1; i >= 0; i--) {
          const key = depths.data.bids[i][0];
          if (bidKeys[key])depths.data.bids.splice(i, 1);
          bidKeys[key] = key;
        }
        if (depths.data.bids.length > DEPTH_LIMIT) depths.data.bids.splice(0, depths.data.bids.length - DEPTH_LIMIT);
      }

      depths.data.lastUpdateId = datum.u;
      depths.data.firstUpdateId = datum.U;
    },
    test: ({
      data: {
        asks,
        bids,
      },
    }) => asks.length > 0 || bids.length > 0,
  });

export const socketReceivedTrade =
  schedule(dispatchScheduled(WEB_SOCKET_RECEIVED_TRADE), {
    // The pushe rate per second could be extremely high,
    // limit to 10 fps
    wait: TRADES_UPDATE_MINIMUM_INTERVAL,
    tolerance: EVENT_LOOP_TOLERANCE,
    maxWait: MAX_WAIT,
    init: () => ({ symbol: null, data: [] }),
    collect: (datum, trades) => {
      trades.symbol = datum.s;
      const { data } = trades;
      data.push(datum);

      // Removes unnecessary data if exceeds the limit
      const { length } = data;
      if (length > TRADES_LIMIT) {
        data.splice(0, length - TRADES_LIMIT);
      }
    },
    test: ({ data }) => data.length > 0,
  });

export const socketReceivedTickers =
  schedule(dispatchScheduled(WEB_SOCKET_RECEIVED_TICKERS), {
    wait: DEPTH_UPDATE_MINIMAL_INTERVAL,
    tolerance: EVENT_LOOP_TOLERANCE,
    maxWait: MAX_WAIT,
    init: () => Object.create(null),
    collect: (data, tickersMap) => {
      const newTickersMap = Object.create(null);
      let i = data.length;
      if (i === 0) {
        return;
      }

      while (--i >= 0) {
        const ticker = data[i];
        if (!(ticker.s in newTickersMap)) {
          // delete '24hrMiniTicker'
          delete ticker.e;
          newTickersMap[ticker.s] = ticker;
        }
      }
      data.length = 0;

      Object.assign(tickersMap, newTickersMap);
    },
    test: tickersMap => Object.keys(tickersMap).length > 0,
  });
